import { useEffect, useState } from "react";
import instance from "../../../../axios/instance";

export const TestimonialForm = ({ formData, onChange }) => {
  const [patients, setPatients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    fetchPatientsAndProducts();
  }, []);

  const fetchPatientsAndProducts = async () => {
    setLoading(true);
    try {
      const [patientsRes, productsRes] = await Promise.all([
        instance.get("/doctor/patient/all", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        instance.get("/doctor/cms/products", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPatients(patientsRes.data || []);
      setProducts(productsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setPatients([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    const updates = { [field]: value };

    onChange({ ...formData, ...updates });
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="space-y-4">
        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title || ""}
            onChange={(e) => handleInputChange("title", e.target.value)}
            placeholder="Enter testimonal title"
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full">
            <label className="block text-[14px] font-medium text-gray-700 mb-1">
              Choose patient <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.patient || ""}
              onChange={(e) => handleInputChange("patient", e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
              disabled={loading}
            >
              <option value="">
                {loading ? "Loading patients..." : "Select a patient"}
              </option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.firstname} {patient.lastname}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full">
            <label className="block text-[14px] font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.category || ""}
              onChange={(e) => handleInputChange("category", e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
              disabled={loading}
            >
              <option value="">
                {loading ? "Loading products..." : "Select a Category"}
              </option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-[13px] text-blue-800">
            <span className="font-medium">Note:</span> You must provide either a
            video URL or an image URL (or both).
          </p>
        </div>

        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Video URL
          </label>
          <input
            type="text"
            value={formData.videoUrl || ""}
            onChange={(e) => handleInputChange("videoUrl", e.target.value)}
            placeholder="https://youtube.com/... or https://vimeo.com/..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter a YouTube, Vimeo, or other video platform URL
          </p>
        </div>

        <div className="flex items-center justify-center">
          <span className="text-gray-400 text-sm font-medium">OR</span>
        </div>

        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Image URL <span className="text-gray-400 font-normal text-[12px] ml-1">(Recommended: 600 x 900 px)</span>
          </label>
          <input
            type="text"
            value={formData.image || ""}
            onChange={(e) => handleInputChange("image", e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter a direct image URL or upload an image (Recommended: 600 x 900 px)
          </p>
        </div>
      </div>

      <div>
        <label className="block text-[14px] font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.description || ""}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Enter patient testimonial description..."
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <span className="text-[14px] font-medium text-gray-700">
            Feature this testimonial
          </span>
          <p className="text-xs text-gray-500 mt-1">
            Featured testimonials are displayed prominently on the website
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.featured || false}
            onChange={(e) => handleInputChange("featured", e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>
    </div>
  );
};
